import { usePage } from '@inertiajs/inertia-vue3';
import { DateTime } from "luxon";
import { EventBus } from '@/eventBus.js';
import axios from 'axios';

const toCurrency = function (value) {
    value = Number(value);
    if (typeof value !== "number") {
        return value;
    }
    var formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
    });
    return formatter.format(value);
};

const roundDecimals = function (value) {
    return Math.round(value * 100) / 100;
};

const formatDate = (date, format = 'MMM d, yyyy') => {
    if (!date) {
        return '-';
    }

    let dateTime;

    // Handle Date objects
    if (date instanceof Date) {
        dateTime = DateTime.fromJSDate(date);
    }
    // Handle numeric timestamps (milliseconds or seconds)
    else if (typeof date === 'number') {
        // If timestamp is in seconds (less than year 2000 in milliseconds), convert to milliseconds
        const timestamp = date < 946684800000 ? date * 1000 : date;
        dateTime = DateTime.fromMillis(timestamp);
    }
    // Handle string dates
    else if (typeof date === 'string') {
        // Try SQL format first (YYYY-MM-DD)
        dateTime = DateTime.fromSQL(date);
        
        // If SQL format fails, try ISO format
        if (dateTime.invalid) {
            dateTime = DateTime.fromISO(date);
        }
        
        // If ISO format fails, try parsing as a general date string
        if (dateTime.invalid) {
            const jsDate = new Date(date);
            if (!isNaN(jsDate.getTime())) {
                dateTime = DateTime.fromJSDate(jsDate);
            }
        }
    }
    // Fallback: try to convert to Date object
    else {
        const jsDate = new Date(date);
        if (!isNaN(jsDate.getTime())) {
            dateTime = DateTime.fromJSDate(jsDate);
        }
    }

    // Check if we have a valid DateTime object
    if (!dateTime || dateTime.invalid) {
        return '-';
    }

    return dateTime.toFormat(format);
};

const formatTimestamp = (timestamp, format = 'MMM. d, yyyy h:mm a') => {
    if (!timestamp) {
        return null;
    }

    let date = DateTime.fromSQL(timestamp).invalid ? DateTime.fromISO(timestamp) : DateTime.fromSQL(timestamp);

    return date.toFormat(format);
};

const formatStatus = (status, capitalize = true) => {
    if (!status || typeof status !== 'string') return status;

    status = status.replaceAll('-', ' ').replaceAll('_', ' ');
    if (capitalize) {
        status = status.toLowerCase().replace(/\b[a-z]/g, (letter) => {
            return letter.toUpperCase();
        });
    }

    return status;
};

// Format lists into strings with commas and 'and'
const formatList = (items) => {
    if (!items.length) return "";
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

// Combine schedule_ordinance and schedule_days into a readable string
const formatOrdinanceWithDays = (ords, scheduleDays) => {
    const ordinance = ords.split(',')
    ordinance[0] = ordinance[0].charAt(0).toUpperCase() + ordinance[0].slice(1)
    var days = scheduleDays.split(',')

    if (ordinance.length > 1) {
        days = days.map(d => d.charAt(0).toUpperCase() + d.slice(1) + 's')
    } else {
        days = days.map(d => d.charAt(0).toUpperCase() + d.slice(1))
    }

    const formattedOrds = formatList(ordinance);
    const formattedDays = formatList(days);

    return formattedOrds + " " + formattedDays + " of the month";
}

const openInNewTab = (url) => {
    window.open(url, '_blank')
}

const shouldAutoprint = (printType = null) => {
    let autoprintEnabled = usePage().props.value.autoprintEnabled;
    let autoprintTypes = ['labels', 'initial_labels'];

    return autoprintEnabled && autoprintTypes.includes(printType);
}

/**
 * Autoprint a label URL; on printer_required open the Select Printer modal and retry.
 *
 * @param {string} url
 * @param {object} params
 * @returns {Promise}
 */
const autoprintWithPrinterPrompt = (url, params = {}) => {
    return axios.get(url, { params }).catch((error) => {
        const data = error.response?.data;
        if (error.response?.status === 409 && data?.status === 'printer_required') {
            return new Promise((resolve, reject) => {
                EventBus.emit('open-select-printer', {
                    mode: 'one-off',
                    type: data.type,
                    printers: data.printers || null,
                    onConfirm: async (printerId, { remember }) => {
                        // Remember already assigned inside the modal when checked.
                        const retryParams = { ...params, printer_id: printerId };
                        await axios.get(url, { params: retryParams });
                        resolve();
                    },
                    onCancel: () => resolve(),
                    onSessionExpired: () => reject(new Error('invalid_checker_session')),
                });
            });
        }

        return Promise.reject(error);
    });
};

/**
 * Open the Select Printer modal in claim mode for a division type.
 *
 * @param {string} type
 * @returns {Promise<object|null>} selected printer payload
 */
const openClaimPrinterModal = (type) => {
    return new Promise((resolve, reject) => {
        EventBus.emit('open-select-printer', {
            mode: 'claim',
            type,
            onConfirm: async (printerId) => {
                resolve({ id: printerId });
            },
            onCancel: () => reject(new Error('printer_selection_cancelled')),
            onSessionExpired: () => reject(new Error('invalid_checker_session')),
        });
    });
};

const asCommaList = function (value) {
    if (!value || typeof value !== 'string' || value.trim() === '') {
        return [];
    }

    return value.split(',').map(v => v.charAt(0).toUpperCase() + v.slice(1).toLowerCase());
};

const formatMinDecimals = function (value, maxDecimals = 2) {
    if (value === null || value === undefined || isNaN(value)) {
        return '';
    }
    // Remove trailing zeros and unnecessary decimals
    return parseFloat(Number(value).toFixed(maxDecimals)).toString();
};

export default {
    methods: { 
        route, 
        toCurrency, 
        roundDecimals, 
        formatDate, 
        formatTimestamp, 
        formatStatus, 
        formatList,
        formatOrdinanceWithDays,
        openInNewTab, 
        shouldAutoprint,
        autoprintWithPrinterPrompt,
        openClaimPrinterModal,
        asCommaList,
        formatMinDecimals
    }
};
